import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, PlugZap, CheckCircle2, ServerCog } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full px-3.5 py-2.5 rounded-md border border-input bg-white/70 focus:outline-none focus:border-foreground/40 focus:ring-1 focus:ring-foreground/20 transition-colors duration-200";

export default function AdminAI() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [url, setUrl] = useState("");
  const [model, setModel] = useState("");
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/ai-config");
      setCfg(data);
      setUrl(data.kimi_base_url || "");
      setModel(data.kimi_model || "");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const save = async () => {
    setSaving(true);
    try {
      const body = { kimi_base_url: url.trim(), kimi_model: model.trim() };
      if (token.trim()) body.kimi_api_key = token.trim();
      const { data } = await api.put("/admin/ai-config", body);
      setCfg(data);
      setToken("");
      toast.success("Kimi3 configuration saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveAndTest = async () => {
    setTesting(true);
    try {
      const body = { kimi_base_url: url.trim(), kimi_model: model.trim() };
      if (token.trim()) body.kimi_api_key = token.trim();
      await api.put("/admin/ai-config", body);
      setToken("");
      const { data: res } = await api.post("/admin/ai-config/test");
      if (res.success) toast.success(res.message);
      else toast.error(res.message || "Kimi3 did not reply as expected.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  if (!cfg) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl px-6 md:px-12 py-10 md:py-14" data-testid="admin-ai-page">
      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Admin</p>
      <h1 className="font-serif text-3xl md:text-4xl text-foreground mb-2 flex items-center gap-3">
        <Sparkles className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} /> AI
      </h1>
      <p className="text-muted-foreground mb-8 max-w-2xl leading-relaxed">
        Configure the shared, self-hosted <span className="font-medium">Kimi3</span> server for everyone.
        Any user who picks "Kimi3 (Local)" as their provider will use this endpoint. No API key is needed,
        but the URL must be reachable from this server (a public URL or a tunnel — not localhost).
      </p>

      <section className="rounded-xl bg-card border border-border p-6" data-testid="admin-ai-kimi-card">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <ServerCog className="w-5 h-5 text-muted-foreground" strokeWidth={1.75} />
            <h2 className="font-serif text-xl text-foreground">Kimi3 (Local) server</h2>
          </div>
          {cfg.configured ? (
            <span className="flex items-center gap-1.5 text-sm font-medium px-2.5 py-1 rounded-full text-emerald-700 bg-emerald-50" data-testid="admin-ai-status">
              <CheckCircle2 className="w-4 h-4" /> Configured
            </span>
          ) : (
            <span className="text-sm text-muted-foreground px-2.5 py-1 rounded-full bg-secondary/60" data-testid="admin-ai-status">
              Not configured
            </span>
          )}
        </div>

        <div className="space-y-4 max-w-lg">
          <div>
            <label className="block text-sm text-foreground/80 mb-1.5">Server URL (OpenAI-compatible)</label>
            <input className={inputCls} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your-kimi-host/v1" data-testid="admin-ai-url" />
            <p className="text-xs text-muted-foreground mt-1">Include the <code>/v1</code> path. Must be internet-reachable.</p>
          </div>
          <div>
            <label className="block text-sm text-foreground/80 mb-1.5">Served model name</label>
            <input className={inputCls} value={model} onChange={(e) => setModel(e.target.value)} placeholder={`e.g. moonshotai/Kimi-K2.6 (default: ${cfg.default_model})`} data-testid="admin-ai-model" />
          </div>
          <div>
            <label className="block text-sm text-foreground/80 mb-1.5 flex items-center gap-2">
              Auth token <span className="text-xs text-muted-foreground">(optional)</span>
              {cfg.has_token && <span className="flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> saved</span>}
            </label>
            <input type="password" className={inputCls} value={token} onChange={(e) => setToken(e.target.value)}
              placeholder={cfg.has_token ? "•••••••• (leave blank to keep)" : "Only if your tunnel/proxy requires a bearer token"}
              data-testid="admin-ai-token" />
            <p className="text-xs text-muted-foreground mt-1">Sent as <code>Authorization: Bearer &lt;token&gt;</code>. Leave blank for an open server.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-6">
          <button onClick={save} disabled={saving}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-foreground text-background font-medium hover:bg-foreground/90 disabled:opacity-50 transition-colors duration-200"
            data-testid="admin-ai-save-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
          <button onClick={saveAndTest} disabled={testing || !url.trim()}
            className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-input bg-white/70 font-medium hover:border-foreground/40 disabled:opacity-50 transition-colors duration-200"
            data-testid="admin-ai-test-btn">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />} Save & test connection
          </button>
        </div>
      </section>
    </div>
  );
}
